import Button from '@/components/UI/Button';
import FileUpload from '@/components/UI/FormElements/FileUpload';
import { Select } from '@/components/UI/FormFields/Latest';
import Header from '@/components/UI/Header';
import { _isNil, _notNil } from '@/littledash';
import type {
  AgentSessionPhase,
  AgentSessionV1,
  AgentStreamEventType,
  AgentStreamEventV1,
  SendDetectionResult,
  SendValidationResult,
} from '@/model/Agent.model';
import { AgentSessionService } from '@/support/AgentSessionService';
import { ResponseType } from '@/support/ApiService';
import { useAgentSession } from '@/support/Hooks/agent/useAgentSession';
import { useAgentStream } from '@/support/Hooks/agent/useAgentStream';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import SendProgress from './SendProgress';
import SendResultsGrid from './SendResultsGrid';
import { sendDomainLabel, sendDomainOptions } from './sendDomains';
import styles from './InVivoSendValidator.module.scss';

const CSV_EXTENSION = '.csv';

/** Local lifecycle for the second (validation) execution that runs on the same session. */
type ValidatePhase = 'idle' | 'confirming' | 'validating' | 'validated' | 'failed';

interface DomainFormValues {
  domainCode: string;
}

const progressPlaceholders: Record<AgentSessionPhase, string> = {
  idle: 'Upload a single-domain SEND CSV to begin.',
  creating: 'Starting detection…',
  streaming: 'Working…',
  completed: '',
  failed: '',
  cancelled: 'Cancelled.',
};

const uploadCsvToS3 = async (file: File, signal?: AbortSignal): Promise<string> => {
  const presigned = await AgentSessionService.presignSendValidatorUpload({ file_name: file.name }, signal);
  if (presigned.type !== ResponseType.Success) {
    throw new Error('Could not mint a presigned upload URL.');
  }
  const { url, key } = presigned.body as { url: string; key: string };
  const putResponse = await fetch(url, { method: 'PUT', body: file, signal });
  if (!putResponse.ok) {
    throw new Error(`S3 upload failed (${putResponse.status})`);
  }
  return key;
};

const asDetectionResult = (value: unknown): SendDetectionResult | null => {
  if (typeof value === 'object' && value !== null && 'detected_domain' in value) {
    return value as SendDetectionResult;
  }
  return null;
};

const asValidationResult = (value: unknown): SendValidationResult | null => {
  if (typeof value === 'object' && value !== null && 'findings' in value && 'summary' in value) {
    return value as SendValidationResult;
  }
  return null;
};

/**
 * Find the detection/validation result by SHAPE across all executions on the session, rather than
 * by array position. A session carries two executions on one thread (detection then validation) and
 * the API returns them newest-first, so a position-based lookup would read the wrong one — the
 * concrete bug behind "no errors detected". Scanning by shape is order-independent.
 */
const findDetectionResult = (session: AgentSessionV1 | null): SendDetectionResult | null => {
  for (const execution of session?.executions ?? []) {
    const detection = asDetectionResult(execution?.result);
    if (_notNil(detection)) {
      return detection;
    }
  }
  return null;
};

const findValidationResult = (session: AgentSessionV1 | null): SendValidationResult | null => {
  for (const execution of session?.executions ?? []) {
    const validation = asValidationResult(execution?.result);
    if (_notNil(validation)) {
      return validation;
    }
  }
  return null;
};

/**
 * The validation execution's api_id on a confirmed session: the execution that is NOT the detection
 * one (its result is not a detection result — either the validation result, or still null while it
 * runs). Used to filter the SSE stream so the detection execution's replayed terminal is ignored.
 */
const findValidationExecutionId = (session: AgentSessionV1 | null): string | null => {
  for (const execution of session?.executions ?? []) {
    if (_isNil(asDetectionResult(execution?.result))) {
      return execution?.api_id ?? null;
    }
  }
  return null;
};

/**
 * Admin-console screen that exercises the SEND Validator agent end-to-end: it
 * uploads a single-domain SEND CSV directly to S3, kicks off the detection
 * execution and streams its progress, pauses for the user to confirm/override
 * the detected SENDIG domain from a closed list, then continues streaming the
 * validation execution on the *same* session and renders the findings grid.
 *
 * The detection execution is driven by `useAgentSession`; because validation is
 * a second execution on the same session (which that single-execution hook does
 * not model), this component subscribes to the same session's SSE stream a
 * second time via `useAgentStream` and refetches the durable session to read the
 * validation result. If `useAgentSession` is later generalised to re-enter
 * `streaming` on a follow-up execution, this local stream handling can be
 * removed in favour of the hook.
 */
export const InVivoSendValidator: FC = () => {
  const { state, createSession, reset } = useAgentSession();
  const { phase: detectPhase, session, streamEvents: detectEvents, error: detectError } = state;

  const [files, setFiles] = useState<Array<File>>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [validatePhase, setValidatePhase] = useState<ValidatePhase>('idle');
  const [validateEvents, setValidateEvents] = useState<Array<AgentStreamEventV1>>([]);
  const [validateError, setValidateError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<SendValidationResult | null>(null);
  const [validateExecutionId, setValidateExecutionId] = useState<string | null>(null);

  const sessionApiId = session?.api_id ?? null;
  const seenValidateIdsRef = useRef<Set<string>>(new Set());

  const domainFormMethods = useForm<DomainFormValues>({
    mode: 'onSubmit',
    // Seeded to the detected domain by the effect below once detection completes.
    defaultValues: { domainCode: '' },
  });

  // Derive the detection result from the durable session during render rather than mirroring it
  // into local state via an effect (avoids a cascading setState-in-effect render).
  const detection = useMemo<SendDetectionResult | null>(() => {
    if (detectPhase !== 'completed') {
      return null;
    }
    const result = findDetectionResult(session);
    return _notNil(result) && result.status === 'requires_input' ? result : null;
  }, [detectPhase, session]);

  useEffect(() => {
    if (_notNil(detection)) {
      domainFormMethods.reset({ domainCode: detection.detected_domain });
    }
  }, [detection, domainFormMethods]);

  const onValidateEvent = useCallback((event: AgentStreamEventV1) => {
    if (_notNil(event.id)) {
      if (seenValidateIdsRef.current.has(event.id)) {
        return;
      }
      seenValidateIdsRef.current.add(event.id);
    }
    setValidateEvents((prev) => [...prev, event]);
  }, []);

  // The stream is filtered (below) to the validation execution, so a terminal event here is the
  // validation execution's own. The server has signalled the run is done; we fetch the durable
  // session to read the validation result (server pushes the update → UI fetches the data).
  const onValidateTerminal = useCallback(
    (eventType: AgentStreamEventType) => {
      if (_isNil(sessionApiId)) {
        return;
      }
      if (eventType !== 'completed') {
        setValidatePhase('failed');
        setValidateError((prev) => prev ?? 'SEND validation did not complete.');
        return;
      }
      const controller = new AbortController();
      AgentSessionService.getSession(sessionApiId, controller.signal).then((response) => {
        if (response.type !== ResponseType.Success || _isNil(response.body)) {
          setValidatePhase('failed');
          setValidateError('Validation finished but the result could not be loaded.');
          return;
        }
        const result = findValidationResult(response.body as AgentSessionV1);
        if (_notNil(result)) {
          setValidationResult(result);
          setValidatePhase('validated');
        } else {
          setValidatePhase('failed');
          setValidateError('Validation finished but no result was returned.');
        }
      });
    },
    [sessionApiId]
  );

  const onValidateError = useCallback((message: string) => {
    setValidateError(message);
    setValidatePhase('failed');
  }, []);

  useAgentStream({
    sessionApiId: validatePhase === 'validating' ? sessionApiId : null,
    executionApiId: validateExecutionId,
    onEvent: onValidateEvent,
    onError: onValidateError,
    onTerminal: onValidateTerminal,
  });

  const isBusy = isUploading || detectPhase === 'creating' || detectPhase === 'streaming';
  const submitDisabled = isBusy || files.length === 0;

  const handleDetect = async () => {
    const file = files[0];
    if (_isNil(file)) {
      return;
    }
    setFileError(null);
    setIsUploading(true);
    let s3Key: string;
    try {
      s3Key = await uploadCsvToS3(file);
    } catch (uploadError) {
      setFileError(uploadError instanceof Error ? uploadError.message : 'Failed to upload CSV.');
      return;
    } finally {
      setIsUploading(false);
    }
    await createSession((signal) => AgentSessionService.createSendValidatorSession({ s3_key: s3Key }, signal));
  };

  const handleConfirmDomain = async (values: DomainFormValues) => {
    if (_isNil(sessionApiId)) {
      return;
    }
    seenValidateIdsRef.current = new Set();
    setValidateEvents([]);
    setValidateError(null);
    setValidatePhase('confirming');
    const response = await AgentSessionService.confirmSendValidatorDomain({
      session_api_id: sessionApiId,
      domain_code: values.domainCode,
    });
    if (response.type === ResponseType.Success && _notNil(response.body)) {
      // The confirm response returns the hydrated session including the freshly-queued validation
      // execution; capture its id so the SSE stream can be filtered to it.
      setValidateExecutionId(findValidationExecutionId(response.body as AgentSessionV1));
      setValidatePhase('validating');
    } else {
      setValidateError(response.error?.message ?? 'Failed to confirm domain.');
      setValidatePhase('failed');
    }
  };

  const handleReset = () => {
    reset();
    setFiles([]);
    setFileError(null);
    setValidatePhase('idle');
    setValidateEvents([]);
    setValidateError(null);
    setValidationResult(null);
    setValidateExecutionId(null);
    seenValidateIdsRef.current = new Set();
    domainFormMethods.reset({ domainCode: '' });
  };

  const showConfirm = _notNil(detection) && _isNil(validationResult) && validatePhase !== 'validated';
  const showResults = _notNil(validationResult);
  const confirmInFlight = validatePhase === 'confirming' || validatePhase === 'validating';

  const combinedEvents = [...detectEvents, ...validateEvents];
  const combinedPhase: AgentSessionPhase =
    validatePhase === 'validating' || validatePhase === 'confirming'
      ? 'streaming'
      : validatePhase === 'validated'
        ? 'completed'
        : validatePhase === 'failed'
          ? 'failed'
          : detectPhase;
  const combinedError = validateError ?? detectError;

  const isTerminal =
    detectPhase === 'completed' || detectPhase === 'failed' || detectPhase === 'cancelled' || showResults;

  return (
    <div
      className="pb3"
      data-test-component="InVivoSendValidator"
      data-test-element="container"
      data-testid="invivo-send-validator"
    >
      <Header
        mainHeaderText="InVivo SEND Validator"
        subHeaderText="Validate a single-domain SEND CSV export against the SENDIG v3.1.1 spec"
      />
      <div className={`${styles.layout} pt4`}>
        <div className="w-50 flex flex-column" data-test-element="left-column">
          <div className="ui-card pa4 flex flex-column" data-test-element="upload">
            <div className="db mb2 fw5">SEND CSV export</div>
            <div className="dark-gray mb2 f6 lh-copy">Upload one single-domain SEND CSV (.csv).</div>
            <FileUpload
              files={files}
              setFiles={(next) => {
                setFileError(null);
                setFiles(next);
              }}
              limit={1}
              disabled={isBusy}
              allowedExtensions={[CSV_EXTENSION]}
              testId="invivo-send-validator__file"
            />
            {_notNil(fileError) && (
              <div className="red mt2" data-testid="invivo-send-validator__file-error">
                {fileError}
              </div>
            )}
            <div className={`${styles.actions} mt4`} data-test-element="actions">
              <Button
                primary
                disabled={submitDisabled}
                loading={isUploading || detectPhase === 'creating'}
                loadingText={isUploading ? 'Uploading' : 'Starting'}
                onClick={handleDetect}
                testId="invivo-send-validator__submit"
              >
                Detect domain
              </Button>
              {isTerminal && (
                <Button plain onClick={handleReset} testId="invivo-send-validator__reset">
                  New file
                </Button>
              )}
            </div>
          </div>

          {showConfirm && (
            <div
              className="ui-card pa4 flex flex-column mt3"
              data-test-element="confirm"
              data-testid="invivo-send-validator__confirm"
            >
              <h2 className="f4 fw5 ma0 mb2">Confirm domain</h2>
              <div className="dark-gray mb3 f6 lh-copy" data-testid="invivo-send-validator__detected-domain">
                Detected domain: <span className="fw5 near-black">{sendDomainLabel(detection.detected_domain)}</span>.
                Keep it or override before validation runs.
              </div>
              <FormProvider {...domainFormMethods}>
                <form onSubmit={domainFormMethods.handleSubmit(handleConfirmDomain)}>
                  <Select
                    name="domainCode"
                    label="Domain"
                    options={sendDomainOptions}
                    value={detection.detected_domain}
                    required
                    disabled={confirmInFlight}
                    testId="invivo-send-validator__domain-select"
                  />
                  <div className={`${styles.actions} mt3`}>
                    <Button
                      submit
                      primary
                      disabled={confirmInFlight}
                      loading={confirmInFlight}
                      loadingText={validatePhase === 'confirming' ? 'Confirming' : 'Validating'}
                      testId="invivo-send-validator__confirm-button"
                    >
                      Confirm and validate
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </div>
          )}
        </div>

        <div className="w-50" data-test-element="right-column">
          <SendProgress
            phase={combinedPhase}
            streamEvents={combinedEvents}
            error={combinedError}
            placeholder={progressPlaceholders}
          />
          {showResults && (
            <div className="mt3">
              <SendResultsGrid result={validationResult} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
